// frontend/src/pages/Auth/OAuthCallback.tsx - FIXED FOR SUPPORT ROLE
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');

  useEffect(() => {
    console.log('🔐 OAuth Callback Page Mounted');
    console.log('🔍 URL Search Params:', window.location.search);
    
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const refreshToken = searchParams.get('refreshToken');
      const needsRoleSelection = searchParams.get('needsRoleSelection') === 'true';
      const redirectTo = searchParams.get('redirectTo') || '/';
      const errorParam = searchParams.get('error');
      const errorMessage = searchParams.get('message');
      const isNewUser = searchParams.get('isNewUser') === 'true';

      console.log('📦 Token received:', token ? 'Yes' : 'No');
      console.log('🔄 Needs role selection:', needsRoleSelection);
      console.log('🎯 Redirect to from backend:', redirectTo);
      console.log('👤 Is new user:', isNewUser);

      if (errorParam) {
        console.error('❌ OAuth error:', errorParam, errorMessage);
        setStatus('error');
        setError(errorMessage || 'Authentication failed');
        setTimeout(() => navigate('/login?error=google_auth_failed'), 3000);
        return;
      }

      if (!token) {
        console.error('❌ No token received');
        setStatus('error');
        setError('No authentication token received');
        setTimeout(() => navigate('/login?error=no_token'), 3000);
        return;
      }

      try {
        console.log('✅ Token found, storing...');
        
        // Store tokens
        localStorage.setItem('token', token);
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }

        // Decode JWT to see role immediately
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('🔑 Decoded JWT payload:', payload);
          console.log('👤 User role from JWT:', payload.role);
        } catch (e) {
          console.log('Could not decode JWT');
        }

        // Fetch user profile
        const apiUrl = import.meta.env.VITE_API_URL || 'https://api.carryngo.ng';
        console.log('📡 Fetching user profile from:', `${apiUrl}/api/auth/me`);
        
        const response = await fetch(`${apiUrl}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user profile');
        }

        const data = await response.json();
        console.log('👤 User data from API:', data);
        
        if (data.success && data.data?.user) {
          const userRole = data.data.user.role;
          console.log('🎭 User role from API:', userRole);
          
          localStorage.setItem('user', JSON.stringify(data.data.user));
          
          // Update auth context
          login(data.data.user, token);
          setStatus('success');
          
          // Determine redirect destination
          let destination = '/';
          
          if (needsRoleSelection || !userRole) {
            console.log('➡️ Redirecting to choose-role (no role selected)');
            destination = '/choose-role';
          } else if (redirectTo !== '/' && redirectTo !== '/choose-role') {
            console.log('➡️ Redirecting to backend-specified path:', redirectTo);
            destination = redirectTo;
          } else {
            // Manual role-based redirect
            switch (userRole) {
              case 'customer':
                console.log('➡️ Redirecting to customer dashboard');
                destination = '/dashboard/customer';
                break;
              case 'rider':
                console.log('➡️ Redirecting to rider dashboard');
                destination = '/dashboard/rider';
                break;
              case 'admin':
                console.log('➡️ Redirecting to admin panel');
                destination = '/admin';
                break;
              case 'support':
                console.log('➡️ Redirecting to support panel');
                destination = '/support';
                break;
              default:
                console.log('➡️ Redirecting to home (unknown role:', userRole, ')');
                destination = '/';
                break;
            }
          }
          
          console.log('🏁 Final redirect destination:', destination);
          
          // Small delay to show success state
          setTimeout(() => {
            navigate(destination);
          }, 500);
        } else {
          throw new Error('Invalid user data');
        }
      } catch (err: any) {
        console.error('❌ OAuth callback error:', err);
        setStatus('error');
        setError(err.message || 'Authentication failed');
        setTimeout(() => navigate('/login?error=auth_failed'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, login]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg">
          <div className="text-red-600 text-5xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Authentication Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="text-gray-500 text-sm">Redirecting to login...</div>
          <div className="mt-4">
            <LoadingSpinner size="sm" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg">
        {status === 'processing' && (
          <>
            <div className="text-green-600 text-5xl mb-4">✅</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Authenticating...</h1>
            <p className="text-gray-600 mb-4">Please wait while we complete your login</p>
            <LoadingSpinner size="lg" />
          </>
        )}
        {status === 'success' && (
          <>
            <div className="text-green-600 text-5xl mb-4">🎉</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Login Successful!</h1>
            <p className="text-gray-600 mb-4">Redirecting you to your dashboard...</p>
            <LoadingSpinner size="sm" />
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;